import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import type { APIError } from "@/types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});

// Request interceptor — attach JWT (user token first, then guest token for
// designai guest engine throttling; a0 endpoints resolve either)
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token =
      localStorage.getItem("fofus_access_token") ||
      localStorage.getItem("fofus_guest_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — unwrap errors, handle 401
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<APIError>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("fofus_access_token");
      localStorage.removeItem("fofus_refresh_token");
      // The silent-SSO probe 401s by design when logged out — NOT a session
      // loss. Redirecting on it bounced anonymous visitors off public pages
      // (designers.fofus.in/ → /login, 09-03).
      const reqUrl = error.config?.url || "";
      const isSsoProbe = reqUrl.includes("/auth/sso/session");
      // GUEST-FIRST LAW (28 Oct): every customer surface is guest-reachable —
      // a boot-time 401 (expired guest token, anon probe) must never hard-bounce
      // the document to /login. The redirect is ONLY for genuinely auth-gated
      // back-office surfaces. Callers already receive {status:401} (flatten law)
      // and show their own in-page login rails (LoginModal), which keep state.
      const p = window.location.pathname;
      const PRIVATE_PREFIXES = ["/designer/", "/admin", "/api/", "/customer/", "/staff"];
      const hardRedirectable = PRIVATE_PREFIXES.some((pre) => p.startsWith(pre));
      if (!isSsoProbe && hardRedirectable && p !== "/login") {
        window.location.href = "/login";
      }
    }
    const rawErr: unknown = error.response?.data;
    // 29 Oct nginx-text responses (404 'Not Found', 502 html) are STRINGS —
    // assigning .status onto them crashed the AccountPage boot boundary
    // ('Cannot create property status on string'). Flatten to an object.
    const apiError: APIError = (rawErr && typeof rawErr === "object")
      ? (rawErr as APIError)
      : { detail: (typeof rawErr === "string" && (rawErr as string).slice(0, 140)) || error.message || "An unexpected error occurred" };
    // carry the HTTP status so callers can branch 401 (signed-out) vs real errors —
    // AccountPage's signed-out CTA depends on it (bug 02 Oct: 401 rendered as 'Try again').
    (apiError as any).status = error.response?.status;
    return Promise.reject(apiError);
  },
);

export default apiClient;

// Convenience: token management helpers
export const tokenStorage = {
  get: () => localStorage.getItem("fofus_access_token"),
  getRefresh: () => localStorage.getItem("fofus_refresh_token"),
  set: (access: string, refresh?: string) => {
    localStorage.setItem("fofus_access_token", access);
    if (refresh) localStorage.setItem("fofus_refresh_token", refresh);
    // Also set cross-domain SSO cookie for *.fofus.in
    setSsoCookie(access);
  },
  clear: () => {
    localStorage.removeItem("fofus_access_token");
    localStorage.removeItem("fofus_refresh_token");
    clearSsoCookie();
  },
};

// ── SSO: cross-subdomain cookie on .fofus.in ──────────────────────────────
// Set when user logs in on any fofusmaster subdomain. Bambuddy (print.fofus.in)
// reads this cookie to auto-login partners without showing its own login page.
export function setSsoCookie(token: string) {
  const expires = new Date();
  expires.setDate(expires.getDate() + 7); // 7 days, matches JWT_EXPIRE_MINUTES
  document.cookie = `fofus_sso=${token}; domain=.fofus.in; path=/; expires=${expires.toUTCString()}; SameSite=Lax; Secure`;
}

export function clearSsoCookie() {
  document.cookie = "fofus_sso=; domain=.fofus.in; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure";
}

export function getSsoCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)fofus_sso=([^;]+)/);
  return match ? match[1] : null;
}